import { useEffect, useMemo, useState } from 'react';
import { colaboradoresApi, type ColaboradorResumo } from '@/features/colaboradores/api/colaboradoresApi';
import { Field } from '@/shared/ui/Field';
import { InputMonetario } from '@/shared/ui/InputMonetario';
import { pdvApi, type CaixaResumo } from '../api/pdvApi';
import { formatarDinheiro, textoDoErro } from '../formato';

type Props = {
  onAberto: (caixa: CaixaResumo) => void;
};

/**
 * Identificação do operador e abertura do caixa.
 *
 * O caixa pertence à pessoa, não à máquina: o navegador não tem
 * identidade de dispositivo (o machineId do web deriva do e-mail), então
 * "por máquina" não teria o mesmo significado nos dois alvos. O PIN é o
 * que dá dono à diferença da gaveta no fim do dia.
 */
export function PdvAberturaCaixa({ onAberto }: Props) {
  const [colaboradores, setColaboradores] = useState<ColaboradorResumo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [colaboradorId, setColaboradorId] = useState('');
  const [pin, setPin] = useState('');
  const [valorAbertura, setValorAbertura] = useState(0);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    let vivo = true;
    colaboradoresApi
      .listar()
      .then((lista) => {
        if (vivo) setColaboradores(lista.filter((c) => c.ativo));
      })
      .catch((e) => {
        if (vivo) setErro(textoDoErro(e));
      })
      .finally(() => {
        if (vivo) setCarregando(false);
      });
    return () => {
      vivo = false;
    };
  }, []);

  const ninguemTemPin = useMemo(
    () => colaboradores.length > 0 && colaboradores.every((c) => !c.temPin),
    [colaboradores],
  );

  async function abrir(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      const { id } = await pdvApi.abrirCaixa({ colaboradorId, pin, valorAbertura });
      onAberto(await pdvApi.obterCaixa(id));
    } catch (err) {
      setErro(textoDoErro(err));
      setPin('');
    } finally {
      setSalvando(false);
    }
  }

  if (carregando) return <div className="pdv-centro pdv-centro--vazio">Carregando…</div>;

  return (
    <div className="pdv-centro">
      <form className="pdv-cartao" onSubmit={abrir}>
        <h2 className="pdv-cartao__titulo">Abrir caixa</h2>
        <p className="pdv-cartao__ajuda">
          O caixa fica no seu nome até você fechar. Quem abre responde pela gaveta.
        </p>

        {ninguemTemPin && (
          <p className="pdv-aviso pdv-aviso--atencao" role="status">
            Nenhum colaborador tem PIN cadastrado. Defina um em <strong>Colaboradores</strong> antes
            de abrir o caixa.
          </p>
        )}

        <Field rotulo="Operador" obrigatorio>
          {(id) => (
            <select
              id={id}
              className="input"
              value={colaboradorId}
              onChange={(e) => setColaboradorId(e.target.value)}
              required
            >
              <option value="">Selecione…</option>
              {colaboradores.map((c) => (
                <option key={c.id} value={c.id} disabled={!c.temPin}>
                  {c.nome}
                  {c.temPin ? '' : ' — sem PIN'}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field rotulo="PIN" obrigatorio>
          {(id) => (
            <input
              id={id}
              className="input pdv-pin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={8}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              placeholder="••••"
              required
            />
          )}
        </Field>

        <Field
          rotulo="Dinheiro na gaveta ao abrir"
          dica="É o troco inicial. Entra no cálculo do que deve estar na gaveta no fechamento."
        >
          {(id) => (
            <InputMonetario
              id={id}
              className="input pdv-num pdv-num--valor"
              valor={valorAbertura}
              onChange={setValorAbertura}
            />
          )}
        </Field>

        {erro && (
          <p className="pdv-aviso pdv-aviso--erro" role="alert">
            {erro}
          </p>
        )}

        <button className="btn btn--primary pdv-botao-grande" type="submit" disabled={salvando}>
          {salvando ? 'Abrindo…' : `Abrir caixa com ${formatarDinheiro(valorAbertura)}`}
        </button>
      </form>
    </div>
  );
}
